/**
 * Permission registry — the single definition of who may do what.
 *
 * The backend enforces these (middleware + service check + query scope filter).
 * The frontend reads the same data to decide what to render. Neither keeps its own copy.
 *
 * See docs/AUTHORIZATION_MATRIX.md. A permission added here without enforcement is
 * caught by the matrix-coverage test.
 */

import { ROLE_RANK, type Role } from './enums';

// ── Scopes ──────────────────────────────────────────────────────────────────────

export const SCOPES = [
  'own',
  'audience',
  'assigned',
  'reports',
  'department',
  'organization',
  'system',
] as const;
export type Scope = (typeof SCOPES)[number];

/**
 * Breadth ordering, used only to pick the widest scope a user holds for a key.
 * `audience` and `assigned` are membership-based rather than hierarchical, so they sit
 * just above `own` by convention.
 */
export const SCOPE_BREADTH: Record<Scope, number> = {
  own: 0,
  audience: 1,
  assigned: 1,
  reports: 2,
  department: 3,
  organization: 4,
  system: 5,
};

// ── Permission keys ─────────────────────────────────────────────────────────────

/** Every permission key, with the scopes that are meaningful for it. */
export const PERMISSIONS = {
  'employees.read': ['own', 'reports', 'department', 'organization'],
  'employees.readSensitive': ['organization'],
  'employees.readHistory': ['own', 'reports', 'department', 'organization'],
  'employees.create': ['organization'],
  'employees.update': ['own', 'organization'],
  'employees.assignRole': ['organization'],
  'employees.manageAccount': ['organization'],
  'employees.setStatus': ['organization'],
  'employees.assignManager': ['organization'],
  'employees.export': ['own', 'reports', 'department', 'organization'],

  'departments.read': ['organization'],
  'departments.manage': ['organization'],
  'departments.delete': ['organization'],
  'departments.assignHead': ['organization'],
  'organization.readChart': ['organization'],
  'team.read': ['reports', 'department', 'organization'],

  'attendance.punch': ['own'],
  'attendance.read': ['own', 'reports', 'department', 'organization'],
  'attendance.requestCorrection': ['own'],
  'attendance.approveCorrection': ['reports', 'department', 'organization'],
  'attendance.correctDirect': ['organization'],
  'attendance.export': ['own', 'reports', 'department', 'organization'],
  'attendance.configure': ['system'],

  'leave.create': ['own'],
  'leave.read': ['own', 'reports', 'department', 'organization'],
  'leave.cancel': ['own'],
  'leave.approve': ['reports', 'department', 'organization'],
  'leave.readBalance': ['own', 'reports', 'department', 'organization'],
  'leave.adjustBalance': ['organization'],
  'leave.configure': ['system'],
  'leave.export': ['own', 'reports', 'department', 'organization'],

  'tasks.read': ['own', 'reports', 'department', 'organization'],
  'tasks.create': ['own', 'reports', 'department', 'organization'],
  'tasks.update': ['own', 'reports', 'department', 'organization'],
  'tasks.assign': ['reports', 'department', 'organization'],
  'tasks.delete': ['own', 'reports', 'department', 'organization'],
  'tasks.comment': ['own', 'reports', 'department', 'organization'],

  'workReports.create': ['own'],
  'workReports.read': ['own', 'reports', 'department', 'organization'],
  'workReports.update': ['own'],
  'workReports.review': ['reports', 'department', 'organization'],
  'workReports.export': ['own', 'reports', 'department', 'organization'],

  'approvals.read': ['own', 'assigned', 'organization'],
  'approvals.decide': ['assigned'],
  'approvals.reassign': ['organization'],
  'approvals.configure': ['system'],

  'announcements.read': ['audience', 'organization'],
  'announcements.manage': ['organization'],
  'announcements.readAcks': ['organization'],

  'notifications.read': ['own'],
  'notifications.configure': ['own'],
  'notifications.configurePolicy': ['system'],

  'workSessions.control': ['own'],
  'workSessions.read': ['own', 'reports', 'department', 'organization'],

  'documents.read': ['own', 'organization'],
  'documents.upload': ['own', 'organization'],
  'documents.manage': ['organization'],
  'documents.download': ['own', 'organization'],

  'onboarding.read': ['own', 'reports', 'department', 'organization'],
  'onboarding.manage': ['organization'],

  'holidays.read': ['organization'],
  'holidays.manage': ['organization'],

  'analytics.readOwn': ['own'],
  'analytics.readScoped': ['reports', 'department', 'organization'],
  'analytics.readExecutive': ['organization'],

  'settings.read': ['system'],
  'settings.configure': ['system'],
  'settings.configureEmail': ['system'],

  'audit.read': ['system'],
  'audit.export': ['system'],
  'roles.read': ['system'],

  'support.tickets.create': ['own'],
  'support.tickets.read': ['own'],
  'support.tickets.readTechnical': ['system'],
  'support.tickets.readHr': ['organization'],
  'support.tickets.manageTechnical': ['system'],
  'support.tickets.manageHr': ['organization'],
  'support.notifications.configure': ['system'],
} as const satisfies Record<string, readonly Scope[]>;

export type PermissionKey = keyof typeof PERMISSIONS;

/** A permission key bound to a scope, e.g. `employees.read:reports`. */
export type ScopedPermission = {
  [K in PermissionKey]: `${K}:${(typeof PERMISSIONS)[K][number]}`;
}[PermissionKey];

export const PERMISSION_KEYS = Object.keys(PERMISSIONS) as PermissionKey[];

// ── Role compositions ───────────────────────────────────────────────────────────

/**
 * Baseline for every signed-in employee. Managers and Department Heads are SCOPES,
 * not roles — see MANAGER_GRANTS / DEPARTMENT_HEAD_GRANTS below.
 */
const EMPLOYEE_BASE = [
  'employees.read:own',
  'employees.update:own',
  'employees.readHistory:own',
  'employees.export:own',
  'departments.read:organization',
  'organization.readChart:organization',
  'attendance.punch:own',
  'attendance.read:own',
  'attendance.requestCorrection:own',
  'attendance.export:own',
  'leave.create:own',
  'leave.read:own',
  'leave.cancel:own',
  'leave.readBalance:own',
  'leave.export:own',
  'tasks.read:own',
  'tasks.create:own',
  'tasks.update:own',
  'tasks.delete:own',
  'tasks.comment:own',
  'workReports.create:own',
  'workReports.read:own',
  'workReports.update:own',
  'workReports.export:own',
  'approvals.read:own',
  'announcements.read:audience',
  'notifications.read:own',
  'notifications.configure:own',
  'workSessions.control:own',
  'workSessions.read:own',
  'documents.read:own',
  // Without this nobody could upload a document at all: the key was declared, the
  // endpoint required it, and no role granted it — so the upload control in the
  // onboarding checklist was visible to every employee and refused by the server.
  // AUTHORIZATION_MATRIX.md §HR records has always specified it.
  'documents.upload:own',
  'documents.download:own',
  'onboarding.read:own',
  'holidays.read:organization',
  'analytics.readOwn:own',
  'support.tickets.create:own',
  'support.tickets.read:own',
] as const satisfies readonly ScopedPermission[];

/**
 * Granted to any employee who has at least one direct report. Derived from `managerId`
 * — never assigned, never a role.
 */
export const MANAGER_GRANTS = [
  'employees.read:reports',
  'employees.readHistory:reports',
  'employees.export:reports',
  'team.read:reports',
  'attendance.read:reports',
  'attendance.approveCorrection:reports',
  'attendance.export:reports',
  'leave.read:reports',
  'leave.approve:reports',
  'leave.readBalance:reports',
  'leave.export:reports',
  'tasks.read:reports',
  'tasks.create:reports',
  'tasks.update:reports',
  'tasks.assign:reports',
  'tasks.delete:reports',
  'tasks.comment:reports',
  'workReports.read:reports',
  'workReports.review:reports',
  'workReports.export:reports',
  'approvals.read:assigned',
  'approvals.decide:assigned',
  'workSessions.read:reports',
  'onboarding.read:reports',
  'analytics.readScoped:reports',
] as const satisfies readonly ScopedPermission[];

/** Granted to the assigned head of a department. An organizational assignment, not a role. */
export const DEPARTMENT_HEAD_GRANTS = [
  'employees.read:department',
  'employees.readHistory:department',
  'employees.export:department',
  'team.read:department',
  'attendance.read:department',
  'attendance.approveCorrection:department',
  'attendance.export:department',
  'leave.read:department',
  'leave.approve:department',
  'leave.readBalance:department',
  'leave.export:department',
  'tasks.read:department',
  'tasks.create:department',
  'tasks.update:department',
  'tasks.assign:department',
  'tasks.delete:department',
  'tasks.comment:department',
  'workReports.read:department',
  'workReports.review:department',
  'workReports.export:department',
  'approvals.read:assigned',
  'approvals.decide:assigned',
  'workSessions.read:department',
  'onboarding.read:department',
  'analytics.readScoped:department',
] as const satisfies readonly ScopedPermission[];

export const ROLE_PERMISSIONS: Record<Role, readonly ScopedPermission[]> = {
  EMPLOYEE: EMPLOYEE_BASE,

  HR: [
    ...EMPLOYEE_BASE,
    'employees.read:organization',
    'employees.readSensitive:organization',
    'employees.readHistory:organization',
    'employees.create:organization',
    'employees.update:organization',
    'employees.assignRole:organization',
    'employees.setStatus:organization',
    'employees.assignManager:organization',
    'employees.export:organization',
    'departments.read:organization',
    'departments.manage:organization',
    'departments.assignHead:organization',
    'team.read:organization',
    'attendance.read:organization',
    'attendance.approveCorrection:organization',
    'attendance.correctDirect:organization',
    'attendance.export:organization',
    'attendance.configure:system',
    'leave.read:organization',
    'leave.approve:organization',
    'leave.readBalance:organization',
    'leave.adjustBalance:organization',
    'leave.configure:system',
    'leave.export:organization',
    'tasks.read:organization',
    'tasks.create:organization',
    'tasks.update:organization',
    'tasks.assign:organization',
    'tasks.delete:organization',
    'workReports.read:organization',
    'workReports.review:organization',
    'workReports.export:organization',
    'approvals.read:assigned',
    'approvals.decide:assigned',
    'approvals.reassign:organization',
    'announcements.read:organization',
    'announcements.manage:organization',
    'announcements.readAcks:organization',
    'notifications.configurePolicy:system',
    'workSessions.read:organization',
    'documents.read:organization',
    'documents.manage:organization',
    'documents.download:organization',
    'onboarding.read:organization',
    'onboarding.manage:organization',
    'holidays.manage:organization',
    'analytics.readScoped:organization',
    'analytics.readExecutive:organization',
    'support.tickets.readHr:organization',
    'support.tickets.manageHr:organization',
    // Deliberately NOT granted to HR:
    //   employees.manageAccount  — portal activation/deactivation is Admin+ only
    //   settings.configureEmail  — HR never receives SMTP configuration
    //   audit.read               — HR never receives system administration
  ],

  CEO: [
    ...EMPLOYEE_BASE,
    'employees.read:organization',
    'employees.readHistory:organization',
    'employees.export:organization',
    'departments.read:organization',
    'team.read:organization',
    'attendance.read:organization',
    'attendance.export:organization',
    'leave.read:organization',
    'leave.approve:organization',
    'leave.readBalance:organization',
    'leave.export:organization',
    'tasks.read:organization',
    'tasks.create:organization',
    'tasks.update:organization',
    'tasks.assign:organization',
    'workReports.read:organization',
    'workReports.review:organization',
    'workReports.export:organization',
    'approvals.read:assigned',
    'approvals.decide:assigned',
    'approvals.reassign:organization',
    'announcements.read:organization',
    'announcements.manage:organization',
    'announcements.readAcks:organization',
    'workSessions.read:organization',
    'analytics.readScoped:organization',
    'analytics.readExecutive:organization',
    'support.tickets.readTechnical:system',
    'support.tickets.readHr:organization',
    'support.tickets.manageTechnical:system',
    'support.tickets.manageHr:organization',
    // Deliberately NOT granted to the CEO:
    //   employees.readSensitive  — seniority is not a need to know (Q-03)
    //   documents.*              — identity paperwork is an HR function
    //   settings.*, audit.*      — no system administration, no SMTP
  ],

  ADMIN: [
    ...EMPLOYEE_BASE,
    'employees.read:organization',
    'employees.readHistory:organization',
    'employees.create:organization',
    'employees.update:organization',
    'employees.assignRole:organization',
    'employees.manageAccount:organization',
    'employees.setStatus:organization',
    'employees.assignManager:organization',
    'employees.export:organization',
    'departments.manage:organization',
    'departments.delete:organization',
    'departments.assignHead:organization',
    'team.read:organization',
    'attendance.read:organization',
    'attendance.correctDirect:organization',
    'attendance.export:organization',
    'attendance.configure:system',
    'leave.read:organization',
    'leave.readBalance:organization',
    'leave.adjustBalance:organization',
    'leave.configure:system',
    'leave.export:organization',
    'tasks.read:organization',
    'tasks.create:organization',
    'tasks.update:organization',
    'tasks.assign:organization',
    'tasks.delete:organization',
    'workReports.read:organization',
    'workReports.export:organization',
    'approvals.read:organization',
    'approvals.reassign:organization',
    'approvals.configure:system',
    'announcements.read:organization',
    'announcements.manage:organization',
    'announcements.readAcks:organization',
    'notifications.configurePolicy:system',
    'workSessions.read:organization',
    'documents.read:organization',
    'documents.manage:organization',
    'documents.download:organization',
    'onboarding.read:organization',
    'onboarding.manage:organization',
    'holidays.manage:organization',
    'analytics.readScoped:organization',
    'analytics.readExecutive:organization',
    'settings.read:system',
    'settings.configure:system',
    'settings.configureEmail:system',
    'audit.read:system',
    'roles.read:system',
    'support.tickets.readTechnical:system',
    'support.tickets.readHr:organization',
    'support.tickets.manageTechnical:system',
    'support.tickets.manageHr:organization',
    'support.notifications.configure:system',
    // Deliberately NOT granted to Admin:
    //   employees.readSensitive  — system administration is not a need to know
    //   leave.approve / approvals.decide / workReports.review
    //     An Admin is NOT an implicit approver. Intervening requires the explicit,
    //     separately audited override action.
  ],

  SUPER_ADMIN: [
    'employees.read:organization',
    'employees.readSensitive:organization',
    'employees.readHistory:organization',
    'employees.create:organization',
    'employees.update:organization',
    'employees.assignRole:organization',
    'employees.manageAccount:organization',
    'employees.setStatus:organization',
    'employees.assignManager:organization',
    'employees.export:organization',
    'departments.read:organization',
    'departments.manage:organization',
    'departments.delete:organization',
    'departments.assignHead:organization',
    'organization.readChart:organization',
    'attendance.configure:system',
    'leave.configure:system',
    'leave.adjustBalance:organization',
    'approvals.reassign:organization',
    'approvals.configure:system',
    'announcements.read:organization',
    'notifications.read:own',
    'notifications.configure:own',
    'notifications.configurePolicy:system',
    'holidays.read:organization',
    'holidays.manage:organization',
    'settings.read:system',
    'settings.configure:system',
    'settings.configureEmail:system',
    'audit.read:system',
    'audit.export:system',
    'roles.read:system',
    // SUPER_ADMIN operates OUTSIDE the employee hierarchy — no support ticket queues: no operational approvals,
    // no team scopes, and no implicit manager relationship with anyone.
  ],
};

// ── Helpers ─────────────────────────────────────────────────────────────────────

export interface GrantedPermissions {
  readonly keys: readonly ScopedPermission[];
}

/** Split `employees.read:reports` into its key and scope. */
export function parsePermission(scoped: string): { key: PermissionKey; scope: Scope } | null {
  const idx = scoped.lastIndexOf(':');
  if (idx === -1) return null;
  const key = scoped.slice(0, idx) as PermissionKey;
  const scope = scoped.slice(idx + 1) as Scope;
  if (!(key in PERMISSIONS)) return null;
  if (!SCOPES.includes(scope)) return null;
  return { key, scope };
}

/**
 * Resolve the permission set for a user. Manager and Department Head grants are added
 * from organizational facts, not from the role.
 */
export function resolvePermissions(opts: {
  role: Role;
  hasDirectReports?: boolean;
  headsDepartment?: boolean;
}): ScopedPermission[] {
  const set = new Set<ScopedPermission>(ROLE_PERMISSIONS[opts.role]);
  if (opts.hasDirectReports) for (const p of MANAGER_GRANTS) set.add(p);
  if (opts.headsDepartment) for (const p of DEPARTMENT_HEAD_GRANTS) set.add(p);
  return [...set];
}

/**
 * Does the user hold this key at all? When `scope` is given, the user must hold that
 * exact scope or a broader one.
 *
 * NOTE: this answers "may your role do this at all". It does NOT answer "may you do it
 * to THIS record" — that is the query scope filter's job. Both are required.
 */
export function hasPermission(
  granted: readonly ScopedPermission[],
  key: PermissionKey,
  scope?: Scope,
): boolean {
  const held = granted
    .map(parsePermission)
    .filter((p): p is { key: PermissionKey; scope: Scope } => p !== null && p.key === key);
  if (held.length === 0) return false;
  if (!scope) return true;
  const needed = SCOPE_BREADTH[scope];
  return held.some((p) => SCOPE_BREADTH[p.scope] >= needed);
}

/** The widest scope the user holds for a key, or null. Drives query scope filters. */
export function widestScope(
  granted: readonly ScopedPermission[],
  key: PermissionKey,
): Scope | null {
  let best: Scope | null = null;
  for (const scoped of granted) {
    const p = parsePermission(scoped);
    if (!p || p.key !== key) continue;
    if (best === null || SCOPE_BREADTH[p.scope] > SCOPE_BREADTH[best]) best = p.scope;
  }
  return best;
}

// ── Rank rules ──────────────────────────────────────────────────────────────────

/**
 * An actor may never create, modify, deactivate or assign a role to a user of EQUAL or
 * HIGHER rank. Enforced on every employee and user mutation, including generic update
 * paths — which is where this check is usually missing and how privilege escalation
 * arrives. See docs/AUTHORIZATION_MATRIX.md §5.
 */
export function canActOnRank(actorRole: Role, targetRole: Role): boolean {
  return ROLE_RANK[actorRole] > ROLE_RANK[targetRole];
}

/** Which roles may this actor assign? */
export function assignableRoles(actorRole: Role): Role[] {
  switch (actorRole) {
    case 'SUPER_ADMIN':
      return ['SUPER_ADMIN', 'ADMIN', 'CEO', 'HR', 'EMPLOYEE'];
    case 'ADMIN':
      return ['CEO', 'HR', 'EMPLOYEE'];
    case 'HR':
      return ['EMPLOYEE'];
    default:
      return [];
  }
}

export function canAssignRole(actorRole: Role, targetRole: Role): boolean {
  return assignableRoles(actorRole).includes(targetRole);
}
